import React, { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Divider,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material'
import {
  Timeline as TimelineIcon,
  TrendingUp as TrendingUpIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Assessment as AssessmentIcon,
  History as HistoryIcon,
} from '@mui/icons-material'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const VersionAnalysis = () => {
  const [versionData, setVersionData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [analysisContent, setAnalysisContent] = useState('')
  const [currentTab, setCurrentTab] = useState(0)

  // Helper function to calculate improvement percentage
  const calculateImprovement = (versions) => {
    if (versions.length < 2) return "0%"
    
    const sortedVersions = [...versions].sort((a, b) => (a.avgOverhead || 0) - (b.avgOverhead || 0))
    const best = sortedVersions[0].avgOverhead || 0
    const worst = sortedVersions[sortedVersions.length - 1].avgOverhead || 0
    
    if (worst === 0) return "0%"
    
    const improvement = ((worst - best) / Math.abs(worst)) * 100
    return `${improvement.toFixed(1)}%`
  }

  useEffect(() => {
    const loadVersionData = async () => {
      try {
        setLoading(true)
        setError(null)
        
        // Load the actual version comparison data from the generated JSON
        const response = await fetch('./data/version-comparison.json')
        if (!response.ok) {
          if (response.status === 404) {
            // No data available - this is expected when no benchmarks have been run
            setError({
              type: 'no-data',
                      message: 'No benchmark data is currently available. This usually means the data source is not accessible or the latest results are still being processed.',
        details: 'The version analysis page requires benchmark data to be loaded from external sources. Check back later after data becomes available.'
            })
          } else {
            throw new Error(`Failed to load version data: ${response.status}`)
          }
          return
        }
        
        const data = await response.json()
        
        console.log('Raw version data loaded:', data)
        
        // Transform the data to match the expected format
        const transformedData = {
          versions: data.versions.map(version => ({
            version: version.version,
            basicOverhead: version.avgOverhead || 0,
            nestedOverhead: version.avgNestedOverhead || 0,
            memoryOverhead: version.memoryOverheadMB || 0,
            nestedMemoryOverhead: 0, // Not available in current data structure
            baselineTime: version.baselineTime || null,
            baselineMemory: version.baselineMemory || 0,
            status: "stable",
            testDate: version.testDate || data.generatedAt || new Date().toISOString(),
            benchmarkCount: version.benchmarkCount || 0,
            iterations: version.iterations || 1,
            benchmarks: version.benchmarks || []
          })),
          summary: {
            bestVersion: data.versions.reduce((best, v) => 
              (v.avgOverhead || 0) < (best.avgOverhead || 0) ? v : best
            ).version,
            worstVersion: data.versions.reduce((worst, v) => 
              (v.avgOverhead || 0) > (worst.avgOverhead || 0) ? v : worst
            ).version,
            improvement: calculateImprovement(data.versions.map(v => ({ avgOverhead: v.avgOverhead }))),
            trend: "improving"
          }
        }
        
        console.log('Transformed data:', transformedData)
        
        setVersionData(transformedData)
        
        // Load the analysis content from the markdown file
        const analysisResponse = await fetch('./NODEJS_ASYNCLOCALSTORAGE_ANALYSIS.md')
        if (analysisResponse.ok) {
          const analysisText = await analysisResponse.text()
          setAnalysisContent(analysisText)
        } else {
          // Fallback to embedded content if file not found
          setAnalysisContent(`# AsyncLocalStorage Changes Analysis Across Node.js Versions

This document provides a comprehensive analysis of AsyncLocalStorage changes across the Node.js versions tested in \`test-versions.sh\`, including code-level modifications and links to relevant files in the Node.js repository.

## Overview

AsyncLocalStorage is a crucial feature in Node.js that provides asynchronous context tracking, allowing developers to maintain state across asynchronous operations. This analysis covers the evolution of AsyncLocalStorage across the following versions tested in our benchmark suite:

- Node.js v16.20.2 (LTS - First version with AsyncLocalStorage stable)
- Node.js v18.19.1 (LTS - Performance improvements)
- Node.js v20.11.0 (LTS - Latest stable)
- Node.js v21.7.3 (Current - Latest features)
- Node.js v22.0.0 (Latest - Cutting edge)
- Node.js v24.6.0 (Latest LTS - Most recent)

## Key Files in Node.js Repository

The primary implementation of AsyncLocalStorage can be found in:
- **Main Implementation**: [\`lib/async_hooks.js\`](https://github.com/nodejs/node/blob/main/lib/async_hooks.js)
- **Internal Implementation**: [\`lib/internal/async_hooks.js\`](https://github.com/nodejs/node/blob/main/lib/internal/async_hooks.js)
- **Tests**: [\`test/parallel/test-async-local-storage*.js\`](https://github.com/nodejs/node/tree/main/test/parallel)
- **Documentation**: [\`doc/api/async_context.md\`](https://github.com/nodejs/node/blob/main/doc/api/async_context.md)

## Version-by-Version Analysis

### Node.js v16.20.2 (LTS - June 2023)

**Status**: Stable AsyncLocalStorage implementation

**Key Characteristics**:
- AsyncLocalStorage was already marked as stable since v16.4.0
- Contains the foundational implementation with core methods:
  - \`run(store, callback[, ...args])\`
  - \`getStore()\`
  - \`enterWith(store)\`
  - \`exit(callback[, ...args])\`
  - \`disable()\`

**Repository Links**:
- [AsyncLocalStorage Implementation](https://github.com/nodejs/node/blob/v16.20.2/lib/async_hooks.js)
- [Test Suite](https://github.com/nodejs/node/blob/v16.20.2/test/parallel/)

**Notable Issues in v16.x**:
- Context loss with Jest testing framework ([Issue #11435](https://github.com/jestjs/jest/issues/11435))
- Some edge cases with debugger attachment

### Node.js v18.19.1 (LTS - February 2024)

**Major Changes from v16.20.2**:

1. **Debugger Context Loss Fix**:
   - Fixed issue where attaching a debugger after using \`await\` caused AsyncLocalStorage to lose context
   - **Issue**: [#43148](https://github.com/nodejs/node/issues/43148)

2. **Introduction of \`bind()\` and \`snapshot()\` Methods** (v18.16.0):
   - \`AsyncLocalStorage.bind(fn)\`: Binds a function to current execution context
   - \`AsyncLocalStorage.snapshot()\`: Captures current execution context and returns wrapper function
   - **File**: [\`lib/async_hooks.js\`](https://github.com/nodejs/node/blob/v18.19.1/lib/async_hooks.js)

3. **Performance Optimizations**:
   - Improved context propagation mechanism
   - Better handling of nested async operations

### Node.js v20.11.0 (LTS - February 2024)

**Major Changes from v18.19.1**:

1. **Enhanced Async Context Handling**:
   - Significant improvements in async context handling for \`setTimeout\`, \`setImmediate\`, and event emitters
   - More predictable behavior in execution order

2. **Bug Fixes for Context Propagation**:
   - Fixed inconsistent propagation of nested async contexts
   - Improved promise resolution behavior with AsyncLocalStorage

3. **Internal Implementation Refinements**:
   - Better memory management for context tracking
   - Reduced overhead in context switching

**Performance Impact**:
- Addressed known performance issues where AsyncLocalStorage could cause up to 97% performance degradation in some async scenarios

### Node.js v21.7.3 (Current - April 2024)

**Major Changes from v20.11.0**:

1. **Stabilization of \`snapshot()\` Method**:
   - The \`snapshot()\` method was marked as stable (no longer experimental)
   - Enhanced reliability for context capture scenarios

2. **Internal Optimizations**:
   - Further performance improvements in context tracking
   - Better handling of high-concurrency scenarios

### Node.js v22.0.0 (Latest - April 2024)

**Major Changes from v21.7.3**:

1. **AsyncResource Behavior Modification**:
   - Fixed \`AsyncResource\` behavior with AsyncLocalStorage to prevent unintended state mutations
   - \`AsyncResource\` now captures state at construction and restores it around each run

2. **Preparation for AsyncContextFrame**:
   - Internal refactoring in preparation for the switch to \`AsyncContextFrame\` (implemented in v24.0.0)
   - Foundation work for significant performance improvements

### Node.js v24.6.0 (Latest LTS - August 2024)

**Major Changes from v22.0.0**:

1. **AsyncContextFrame Implementation** (v24.0.0):
   - **Revolutionary Change**: Complete internal rewrite to use \`AsyncContextFrame\` instead of traditional async hooks
   - **Performance**: Dramatic performance improvements - addresses the ~97% performance degradation issue
   - **Reliability**: Fixes context mixing issues during concurrent API calls
   - **Pull Request**: [#55552](https://github.com/nodejs/node/pull/55552)

2. **Backward Compatibility Flag** (v24.0.1):
   - **Flag**: \`--no-async-context-frame\` introduced to revert to old behavior if needed
   - **Reason**: Some existing code using \`AsyncResource\` with \`AsyncLocalStorage\` may break
   - **Issue**: [#58204](https://github.com/nodejs/node/issues/58204)

3. **Context Management Improvements**:
   - Eliminates context loss in high-concurrency scenarios
   - Better performance under heavy async workloads
   - More predictable behavior across all async operations

## Summary of Major Changes

### Performance Evolution
1. **v16.20.2**: Baseline stable implementation with known performance overhead
2. **v18.19.1**: Initial performance optimizations and new methods
3. **v20.11.0**: Significant async context handling improvements
4. **v21.7.3**: Continued optimization for high-concurrency scenarios
5. **v22.0.0**: Foundation for major performance overhaul
6. **v24.6.0**: Revolutionary AsyncContextFrame implementation - dramatic performance improvements

### API Evolution
1. **v16.20.2**: Core methods (\`run\`, \`getStore\`, \`enterWith\`, \`exit\`, \`disable\`)
2. **v18.19.1**: Added \`bind()\` and \`snapshot()\` methods
3. **v20.11.0**: Enhanced existing method behavior
4. **v21.7.3**: Stabilized \`snapshot()\` method
5. **v22.0.0**: Refined \`AsyncResource\` integration
6. **v24.6.0**: Same API surface, completely rewritten internal implementation with AsyncContextFrame

### Key Issues Addressed
1. **Debugger Context Loss** (v18.x): Fixed context loss when attaching debugger after \`await\`
2. **Execution Order Predictability** (v20.x): Made async operation behavior more consistent
3. **Nested Context Propagation** (v20.x): Fixed inconsistent propagation in complex async scenarios
4. **AsyncResource Integration** (v22.x): Prevented unintended state mutations
5. **Performance Overhead** (v24.x): Revolutionary AsyncContextFrame implementation eliminates ~97% performance degradation

## Testing Implications

When comparing performance across these versions, you should expect:

1. **v16.20.2**: Baseline performance with highest overhead
2. **v18.19.1**: Moderate improvements, especially in debugging scenarios
3. **v20.11.0**: Significant improvements in async operation performance
4. **v21.7.3**: Incremental improvements in high-concurrency scenarios
5. **v22.0.0**: Similar to v21.7.3 but with better AsyncResource behavior
6. **v24.6.0**: **Dramatic performance improvement** - should show the largest performance gains of any version

## Performance Testing Focus Areas

Based on the version changes, your benchmarks should particularly focus on:

1. **Context Propagation Overhead**: How much overhead AsyncLocalStorage adds
2. **Nested Async Operations**: Performance in complex async scenarios
3. **Memory Usage**: Context tracking memory consumption
4. **Concurrent Operations**: Performance under high concurrency
5. **Integration Testing**: Behavior with timers, promises, and event emitters

## Future Considerations

Node.js v24.6.0 represents the culmination of years of AsyncLocalStorage evolution with the AsyncContextFrame implementation. This version should show the most dramatic performance improvements compared to all previous versions. Future versions are likely to build on this foundation with incremental improvements and bug fixes.

**Key Testing Expectations for v24.6.0**:
- Significantly reduced AsyncLocalStorage overhead compared to all previous versions
- Better performance scaling under high concurrency
- More consistent behavior across different async operation types
- Potential breaking changes requiring testing with existing AsyncResource usage patterns

## References

- [Node.js GitHub Repository](https://github.com/nodejs/node)
- [AsyncLocalStorage Documentation](https://nodejs.org/api/async_context.html)
- [Performance Issue Discussion](https://github.com/nodejs/node/issues/34493)
- [Context Loss Issues](https://github.com/nodejs/node/issues/43148)
- [AsyncResource Behavior Changes](https://github.com/nodejs/node/issues/58204)
- [AsyncContextFrame Implementation](https://github.com/nodejs/node/pull/55552)
- [Node.js v24.0.0 Release Notes](https://github.com/nodejs/node/releases/tag/v24.0.0)

---

*This analysis was generated on $(date) as part of the AsyncLocalStorage performance testing suite.*`)
        }
        
      } catch (err) {
        console.error('Error loading version data:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    loadVersionData()
  }, [])

  const getOverheadColor = (overhead) => {
    if (overhead <= 3) return 'success'
    if (overhead <= 8) return 'warning'
    return 'error'
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'stable': return 'success'
      case 'experimental': return 'warning'
      case 'deprecated': return 'error'
      default: return 'default'
    }
  }

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue)
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    // Handle different types of errors
    if (error.type === 'no-data') {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Alert severity="info" sx={{ width: '100%', maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              📊 No Benchmark Data Available
            </Typography>
            <Typography variant="body1" paragraph>
              {error.message}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error.details}
            </Typography>
            <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic' }}>
              This page will automatically populate once benchmark results are available from the data source.
            </Typography>
          </Alert>
        </Box>
      )
    }
    
    // Handle other errors
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Alert severity="error" sx={{ width: '100%' }}>
          <Typography variant="h6" gutterBottom>
            ❌ Error Loading Data
          </Typography>
          <Typography variant="body1">
            {typeof error === 'string' ? error : error.message || 'An unexpected error occurred'}
          </Typography>
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#6750a4', mb: 3 }}>
        Version Analysis
      </Typography>

      <Typography variant="body1" paragraph sx={{ mb: 4 }}>
        Performance comparison across Node.js versions, showing how AsyncLocalStorage performance 
        has evolved and improved over time.
      </Typography>

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange} aria-label="version analysis tabs">
          <Tab 
            icon={<AssessmentIcon />} 
            label="Performance Data" 
            iconPosition="start"
          />
          <Tab 
            icon={<HistoryIcon />} 
            label="Detailed Analysis" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {currentTab === 0 && (
        <Box>


          {!versionData && !loading && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              No version data available. Please check if the data source is accessible.
            </Alert>
          )}

          {/* Metrics Explanation */}
          {versionData && (
            <Paper elevation={2} sx={{ p: 3, mb: 4, backgroundColor: '#f0f8ff' }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#1976d2' }}>
                📊 Understanding the Metrics
              </Typography>
              
              {/* Baseline Explanation */}
              <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#e8f5e8' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32', mb: 1 }}>
                  🎯 What is the Baseline?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  All performance metrics compare AsyncLocalStorage performance against the <strong>same code executed without AsyncLocalStorage</strong>.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Baseline = 0% overhead</strong> (the fastest possible execution)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Baseline Column:</strong> Shows actual execution time and memory usage without AsyncLocalStorage for direct comparison.
                </Typography>
              </Paper>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    ⏱️ Basic ALS Overhead
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>What it measures:</strong> How much slower AsyncLocalStorage makes your code compared to the same code without AsyncLocalStorage.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Example:</strong> 50% overhead means AsyncLocalStorage makes your code 1.5x slower than baseline (50% slower than baseline).
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Baseline:</strong> Code execution time without AsyncLocalStorage.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    🔄 Nested ALS Overhead
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>What it measures:</strong> Performance impact when using multiple nested AsyncLocalStorage contexts (contexts within contexts).
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Example:</strong> 260% overhead means nested contexts make your code 3.6x slower than baseline.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Baseline:</strong> Code execution time without any AsyncLocalStorage.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    💾 Memory Usage
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>What it measures:</strong> Additional memory consumed by AsyncLocalStorage operations compared to baseline.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Example:</strong> +5 MB means AsyncLocalStorage uses 5 MB more memory than baseline.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Baseline:</strong> Memory usage without AsyncLocalStorage.
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          )}



          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <TimelineIcon sx={{ color: '#6750a4', marginRight: 1 }} />
                    <Typography variant="h6">Performance Across Node.js Versions</Typography>
                  </Box>
                  
                  {versionData && (
                    <TableContainer component={Paper} elevation={1}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Node.js Version</strong></TableCell>
                            <TableCell align="right"><strong>Baseline</strong></TableCell>
                            <TableCell align="right"><strong>Basic ALS Overhead</strong></TableCell>
                            <TableCell align="right"><strong>Nested ALS Overhead</strong></TableCell>
                            <TableCell align="right"><strong>Memory Consumption</strong></TableCell>
                            <TableCell align="center"><strong>Iterations</strong></TableCell>
                            <TableCell align="center"><strong>Test Date</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {versionData.versions.map((version, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {version.version}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {version.benchmarkCount} benchmarks
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Baseline
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  Execution time
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  Baseline memory
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={`${(version.basicOverhead || 0).toFixed(1)}%`}
                                  color={getOverheadColor(version.basicOverhead)}
                                  size="small"
                                />
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {version.basicOverhead > 0 ? 'slower' : 'faster'}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {version.basicOverhead > 0 ? 
                                    `${(1 + (version.basicOverhead || 0)/100).toFixed(1)}x baseline` : 
                                    `${(1 - (version.basicOverhead || 0)/100).toFixed(1)}x baseline`
                                  }
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={`${(version.nestedOverhead || 0).toFixed(1)}%`}
                                  color={getOverheadColor(version.nestedOverhead)}
                                  size="small"
                                />
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {version.nestedOverhead > 0 ? 'slower' : 'faster'}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {version.nestedOverhead > 0 ? 
                                    `${(1 + (version.nestedOverhead || 0)/100).toFixed(1)}x baseline` : 
                                    `${(1 - (version.nestedOverhead || 0)/100).toFixed(1)}x baseline`
                                  }
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Box>
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    <strong>Memory Overhead:</strong> {(version.memoryOverhead || 0).toFixed(1)} MB
                                  </Typography>
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                    <strong>Total Tests:</strong> {version.benchmarkCount || 0}
                                  </Typography>
                                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic' }}>
                                    {version.memoryOverhead > 0 ? 'Uses more memory' : 'Memory efficient'}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
                                  {version.iterations || 1}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  test runs
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="caption">
                                  {new Date(version.testDate).toLocaleDateString()}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  {version.benchmarkCount} benchmarks
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}





                  {!versionData && !loading && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Check the data source to see benchmark data for all Node.js versions.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Box display="flex" alignItems="center" sx={{ mb: 2 }}>
                    <TrendingUpIcon sx={{ color: '#6750a4', marginRight: 1 }} />
                    <Typography variant="h6">Key Insights</Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper elevation={1} sx={{ p: 2, backgroundColor: '#e8f5e8' }}>
                        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                          <CheckCircleIcon sx={{ color: '#4caf50', marginRight: 1 }} />
                          <Typography variant="subtitle2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                            Performance Improvements
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {versionData ? 
                            `Node.js ${versionData.summary.bestVersion} shows ${versionData.summary.improvement} better performance compared to Node.js ${versionData.summary.worstVersion}. AsyncLocalStorage has become significantly more efficient over recent versions.` :
                            'Check the data source to see performance improvements across Node.js versions.'
                          }
                        </Typography>
                      </Paper>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Paper elevation={1} sx={{ p: 2, backgroundColor: '#fff3e0' }}>
                        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                          <WarningIcon sx={{ color: '#ff9800', marginRight: 1 }} />
                          <Typography variant="subtitle2" sx={{ color: '#e65100', fontWeight: 600 }}>
                            Version Considerations
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          While newer versions perform better, consider your production requirements. 
                          Node.js 18+ offers the best balance of performance and stability for 
                          AsyncLocalStorage usage.
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {currentTab === 1 && (
        <Box>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" sx={{ mb: 3 }}>
                <HistoryIcon sx={{ color: '#6750a4', marginRight: 1 }} />
                <Typography variant="h6">AsyncLocalStorage Changes Analysis</Typography>
              </Box>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  This analysis covers the evolution of AsyncLocalStorage across Node.js versions, 
                  including code-level modifications and performance improvements.
                </Typography>
              </Alert>

              <Box sx={{ 
                '& h1': { color: '#6750a4', fontSize: '2rem', fontWeight: 'bold', mb: 2, mt: 3 },
                '& h2': { color: '#6750a4', fontSize: '1.5rem', fontWeight: 'bold', mb: 1.5, mt: 2.5 },
                '& h3': { color: '#6750a4', fontSize: '1.25rem', fontWeight: 'bold', mb: 1, mt: 2 },
                '& h4': { color: '#6750a4', fontSize: '1.1rem', fontWeight: 'bold', mb: 0.5, mt: 1.5 },
                '& p': { mb: 1.5, lineHeight: 1.6 },
                '& ul, & ol': { mb: 1.5, pl: 3 },
                '& li': { mb: 0.5 },
                '& strong': { fontWeight: 'bold' },
                '& em': { fontStyle: 'italic' },
                '& code': { 
                  backgroundColor: '#f5f5f5', 
                  padding: '2px 4px', 
                  borderRadius: '3px',
                  fontFamily: 'monospace',
                  fontSize: '0.9em'
                },
                '& pre': { 
                  backgroundColor: '#f5f5f5', 
                  padding: '16px', 
                  borderRadius: '8px',
                  overflow: 'auto',
                  mb: 2
                },
                '& blockquote': { 
                  borderLeft: '4px solid #6750a4', 
                  pl: 2, 
                  ml: 0, 
                  fontStyle: 'italic',
                  backgroundColor: '#f8f9fa',
                  py: 1,
                  pr: 2
                },
                '& a': { color: '#6750a4', textDecoration: 'underline' },
                '& table': { width: '100%', borderCollapse: 'collapse', mb: 2 },
                '& th, & td': { border: '1px solid #ddd', padding: '8px', textAlign: 'left' },
                '& th': { backgroundColor: '#f5f5f5', fontWeight: 'bold' }
              }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {analysisContent}
                </ReactMarkdown>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  )
}

export default VersionAnalysis
